import { useEffect } from 'react';

interface SeoProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noIndex?: boolean;
}

const BASE_URL = 'https://wesaves.com';
const DEFAULT_IMAGE = `${BASE_URL}/brand/og-image.png`;

export function useSeo({ title, description, canonical, ogType = 'website', ogImage = DEFAULT_IMAGE, noIndex = false }: SeoProps) {
  useEffect(() => {
    const fullTitle = title.includes('Wesaves') ? title : `${title} | Wesaves`;
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : `${BASE_URL}${window.location.pathname}`;

    // Title
    document.title = fullTitle;

    // Helper to set or create a meta tag
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        if (selector.startsWith('meta[name')) el.setAttribute('name', attr);
        else el.setAttribute('property', attr);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    // Primary
    setMeta('meta[name="description"]', 'description', description);

    // Robots
    setMeta('meta[name="robots"]', 'robots', noIndex ? 'noindex,nofollow' : 'index,follow');

    // Canonical
    let canonicalEl = document.querySelector<HTMLLinkElement>('#canonical-link');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.id = 'canonical-link';
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = canonicalUrl;

    // Open Graph
    setMeta('meta[property="og:title"]', 'og:title', fullTitle);
    setMeta('meta[property="og:description"]', 'og:description', description);
    setMeta('meta[property="og:url"]', 'og:url', canonicalUrl);
    setMeta('meta[property="og:type"]', 'og:type', ogType);
    setMeta('meta[property="og:image"]', 'og:image', ogImage);

    // Twitter
    setMeta('meta[name="twitter:title"]', 'twitter:title', fullTitle);
    setMeta('meta[name="twitter:description"]', 'twitter:description', description);
    setMeta('meta[name="twitter:image"]', 'twitter:image', ogImage);
  }, [title, description, canonical, ogType, ogImage, noIndex]);
}
